import httpService from './httpService';

export interface PerformanceMetric {
  id: number;
  entityId: number;
  entityType: 'PORTFOLIO' | 'BASKET';
  entityName?: string; // Name of the portfolio or basket
  providerId?: number; // ID of the provider that owns this entity
  providerName?: string; // Name of the provider
  metricName: string;
  metricValue: number; // Will be converted from Decimal on the backend
  metricDate: string;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string | null;
  creator: {
    id: string;
    email: string;
    name?: string | null;
  };
  updater?: {
    id: string;
    email: string;
    name?: string | null;
  } | null;
}

export interface CreatePerformanceMetricData {
  entityId: number;
  entityType: 'PORTFOLIO' | 'BASKET';
  metricName: string;
  metricValue: number;
  metricDate: string;
}

export interface UpdatePerformanceMetricData {
  metricValue: number;
}

export interface PerformanceMetricFilters {
  entityId?: number;
  entityType?: 'PORTFOLIO' | 'BASKET';
  metricName?: string;
  startDate?: string;
  endDate?: string;
  createdBy?: string;
}

class PerformanceMetricsService {
  async createMetric(data: CreatePerformanceMetricData): Promise<PerformanceMetric> {
    const response = await httpService.post<{ metric: PerformanceMetric }>('/performance-metrics', data);
    return response.data.metric;
  }

  async updateMetric(id: number, data: UpdatePerformanceMetricData): Promise<PerformanceMetric> {
    const response = await httpService.put<{ metric: PerformanceMetric }>(`/performance-metrics/${id}`, data);
    return response.data.metric;
  }

  async getMetric(id: number): Promise<PerformanceMetric> {
    const response = await httpService.get<{ metric: PerformanceMetric }>(`/performance-metrics/${id}`);
    return response.data.metric;
  }

  async getMetrics(filters?: PerformanceMetricFilters): Promise<PerformanceMetric[]> {
    const params = new URLSearchParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });
    }

    const response = await httpService.get<{ metrics: PerformanceMetric[] }>(
      `/performance-metrics${params.toString() ? `?${params.toString()}` : ''}`
    );
    return response.data.metrics;
  }

  async deleteMetric(id: number): Promise<void> {
    await httpService.delete(`/performance-metrics/${id}`);
  }

  async getMetricsByEntity(entityId: number, entityType: 'PORTFOLIO' | 'BASKET', requiresAuth: boolean = true): Promise<PerformanceMetric[]> {
    const response = await httpService.get<{ metrics: PerformanceMetric[] }>(
      `/performance-metrics/entity/${entityType}/${entityId}`,
      { requiresAuth }
    );
    return response.data.metrics;
  }

  async getAvailableMetricNames(): Promise<string[]> {
    const response = await httpService.get<{ metricNames: string[] }>('/performance-metrics/metric-names');
    return response.data.metricNames;
  }

  async getMetricsByEntities(entities: Array<{ entityId: number; entityType: 'PORTFOLIO' | 'BASKET' }>): Promise<Array<{ entityId: number; entityType: 'PORTFOLIO' | 'BASKET'; metrics: PerformanceMetric[] }>> {
    const response = await httpService.post<{ results: Array<{ entityId: number; entityType: 'PORTFOLIO' | 'BASKET'; metrics: PerformanceMetric[] }> }>(
      '/performance-metrics/batch',
      { entities }
    );
    return response.data.results;
  }
}

export const performanceMetricsService = new PerformanceMetricsService();
